// Content articles — same `articles` collection the Flutter app reads.
import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Button, Card, Table, TableHead, TableBody, TableRow,
    TableCell, TableContainer, Chip, IconButton, Dialog, DialogTitle,
    DialogContent, DialogActions, TextField, Select, MenuItem, FormControl,
    InputLabel, Alert, CircularProgress, FormControlLabel, Checkbox
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import PublishIcon from '@mui/icons-material/Publish';
import { collection, getDocs, addDoc, updateDoc, deleteDoc, doc, serverTimestamp } from 'firebase/firestore';
import { auth, db } from '../services/firebase';

const CATEGORIES = [
    { value: 'understanding', label: 'Understanding Pregnancy Loss' },
    { value: 'coping', label: 'Coping & Healing' },
    { value: 'pals', label: 'Pregnancy After Loss' },
    { value: 'ttc', label: 'Trying to Conceive' },
    { value: 'pregnancy', label: 'Pregnancy' },
    { value: 'fertility', label: 'Fertility' },
    { value: 'mentalHealth', label: 'Mental Health' },
    { value: 'medical', label: 'Medical Information' },
    { value: 'maleFertility', label: 'Male Fertility' },
    { value: 'couplesFertility', label: 'For Couples' },
    { value: 'lifestyle', label: 'Lifestyle' },
];

const emptyForm = {
    title: '',
    summary: '',
    content: '',
    category: 'ttc',
    tags: '',
    imageUrl: '',
    readTime: 5,
    status: 'draft',
    doctorReviewed: false,
    reviewerName: '',
    reviewerCredentials: '',
};

const slugify = (title) =>
    title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-+|-+$/g, '').slice(0, 60);

const Articles = () => {
    const [articles, setArticles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [success, setSuccess] = useState(null);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(emptyForm);

    const load = async () => {
        setLoading(true);
        try {
            const snap = await getDocs(collection(db, 'articles'));
            const rows = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
            rows.sort((a, b) => (b.updatedAt?.seconds || 0) - (a.updatedAt?.seconds || 0));
            setArticles(rows);
            setError(null);
        } catch (e) {
            console.error(e);
            setError('Failed to load articles');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { load(); }, []);

    const openCreate = () => {
        setEditing(null);
        setForm(emptyForm);
        setOpen(true);
    };

    const openEdit = (article) => {
        setEditing(article);
        setForm({
            title: article.title || '',
            summary: article.summary || '',
            content: article.content || '',
            category: article.category || 'ttc',
            tags: (article.tags || []).join(', '),
            imageUrl: article.imageUrl || '',
            readTime: article.readTime || 5,
            status: article.status || 'draft',
            doctorReviewed: !!article.doctorReviewed,
            reviewerName: article.reviewerName || '',
            reviewerCredentials: article.reviewerCredentials || '',
        });
        setOpen(true);
    };

    const save = async () => {
        const user = auth.currentUser;
        if (!user) {
            setError('Not signed in');
            return;
        }
        if (!form.title.trim() || !form.content.trim()) {
            setError('Title and content are required');
            return;
        }
        const payload = {
            title: form.title.trim(),
            summary: form.summary.trim(),
            content: form.content,
            category: form.category,
            tags: form.tags.split(',').map((t) => t.trim()).filter(Boolean),
            imageUrl: form.imageUrl || null,
            readTime: Number(form.readTime) || 5,
            status: form.status,
            updatedAt: serverTimestamp(),
            doctorReviewed: form.doctorReviewed,
            reviewerName: form.doctorReviewed ? form.reviewerName.trim() || null : null,
            reviewerCredentials: form.doctorReviewed ? form.reviewerCredentials.trim() || null : null,
            slug: slugify(form.title),
            sendNotification: false,
            notificationTargets: ['all'],
            citations: [],
        };
        try {
            if (editing) {
                await updateDoc(doc(db, 'articles', editing.id), payload);
                setSuccess('Article updated');
            } else {
                await addDoc(collection(db, 'articles'), {
                    ...payload,
                    authorId: user.uid,
                    authorName: user.displayName || user.email || 'Admin',
                    createdAt: serverTimestamp(),
                    viewsCount: 0,
                });
                setSuccess('Article created');
            }
            setOpen(false);
            load();
        } catch (e) {
            console.error(e);
            setError('Failed to save article');
        }
    };

    const publish = async (article) => {
        try {
            await updateDoc(doc(db, 'articles', article.id), {
                status: 'published',
                updatedAt: serverTimestamp(),
            });
            setSuccess('Published');
            load();
        } catch (e) {
            setError('Failed to publish');
        }
    };

    const remove = async (article) => {
        if (!window.confirm(`Delete “${article.title}”?`)) return;
        try {
            await deleteDoc(doc(db, 'articles', article.id));
            setSuccess('Deleted');
            load();
        } catch (e) {
            setError('Failed to delete');
        }
    };

    const statusColor = (status) => {
        if (status === 'published') return 'success';
        if (status === 'draft') return 'warning';
        return 'default';
    };

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h4">Articles</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
                    New article
                </Button>
            </Box>
            {error && <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess(null)} sx={{ mb: 2 }}>{success}</Alert>}
            <Card>
                {loading ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}><CircularProgress /></Box>
                ) : articles.length === 0 ? (
                    <Box sx={{ p: 6, textAlign: 'center' }}>
                        <Typography variant="h6" gutterBottom>No articles yet</Typography>
                        <Typography color="text.secondary">
                            Create an article to publish it in the Community Hub.
                        </Typography>
                    </Box>
                ) : (
                    <TableContainer>
                        <Table>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Title</TableCell>
                                    <TableCell>Category</TableCell>
                                    <TableCell>Status</TableCell>
                                    <TableCell align="right">Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {articles.map((article) => (
                                    <TableRow key={article.id}>
                                        <TableCell>{article.title}</TableCell>
                                        <TableCell>{article.category}</TableCell>
                                        <TableCell>
                                            <Chip size="small" label={article.status || 'draft'} color={statusColor(article.status)} />
                                        </TableCell>
                                        <TableCell align="right">
                                            {article.status !== 'published' && (
                                                <IconButton onClick={() => publish(article)} title="Publish">
                                                    <PublishIcon />
                                                </IconButton>
                                            )}
                                            <IconButton onClick={() => openEdit(article)}><EditIcon /></IconButton>
                                            <IconButton onClick={() => remove(article)}><DeleteIcon /></IconButton>
                                        </TableCell>
                                    </TableRow>
                                ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                )}
            </Card>

            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="md" fullWidth>
                <DialogTitle>{editing ? 'Edit article' : 'New article'}</DialogTitle>
                <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 2 }}>
                    <TextField label="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} />
                    <TextField label="Summary" value={form.summary} onChange={(e) => setForm({ ...form, summary: e.target.value })} multiline minRows={2} />
                    <TextField label="Content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} multiline minRows={8} />
                    <FormControl>
                        <InputLabel>Category</InputLabel>
                        <Select label="Category" value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })}>
                            {CATEGORIES.map((c) => <MenuItem key={c.value} value={c.value}>{c.label}</MenuItem>)}
                        </Select>
                    </FormControl>
                    <FormControl>
                        <InputLabel>Status</InputLabel>
                        <Select label="Status" value={form.status} onChange={(e) => setForm({ ...form, status: e.target.value })}>
                            <MenuItem value="draft">Draft</MenuItem>
                            <MenuItem value="published">Published</MenuItem>
                            <MenuItem value="archived">Archived</MenuItem>
                        </Select>
                    </FormControl>
                    <TextField label="Tags (comma separated)" value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} />
                    <TextField label="Image URL" value={form.imageUrl} onChange={(e) => setForm({ ...form, imageUrl: e.target.value })} />
                    <TextField type="number" label="Read time (minutes)" value={form.readTime} onChange={(e) => setForm({ ...form, readTime: e.target.value })} />
                    <FormControlLabel
                        control={<Checkbox checked={form.doctorReviewed} onChange={(e) => setForm({ ...form, doctorReviewed: e.target.checked })} />}
                        label="Clinically reviewed"
                    />
                    {form.doctorReviewed && (
                        <>
                            <TextField label="Reviewer name" value={form.reviewerName} onChange={(e) => setForm({ ...form, reviewerName: e.target.value })} />
                            <TextField label="Reviewer credentials" value={form.reviewerCredentials} onChange={(e) => setForm({ ...form, reviewerCredentials: e.target.value })} />
                        </>
                    )}
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
                    <Button variant="outlined" onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" onClick={save}>Save</Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default Articles;
